import React from 'react';
import { ArrowRight, Check, Clock, Users, MapPin, Target, Compass, ChevronRight } from 'lucide-react';
import { PageTransition } from '../../components/layout/PageTransition';
import { Button } from '../../components/ui/Button';
import { Card } from '../../components/ui/Card';
import { Animate } from '../../components/ui/Animate';
import { Link } from 'react-router-dom';
import { useLanguage } from '../../context/LanguageContext';
export function StrategyWorkshop() {
  const {
    language
  } = useLanguage();
  const en = {
    badge: 'Strategy',
    title: 'Strategy Facilitation',
    titleHighlight: 'Workshop',
    intro1: 'Most strategy offsites produce a document. What they rarely produce is a strategy — a genuine choice about where to play and how to win, with the hard trade-offs made explicit and the logic clear enough that people can act on it independently.',
    intro2: 'This workshop is built around making those choices together, with the right frameworks and the right questions. It draws on Rumelt\'s kernel of good strategy, Roger Martin\'s Playing to Win, and Wardley mapping for landscape awareness — adapted to the scale and context of Icelandic organisations.',
    heroCta: 'Get in touch',
    whatTitle: 'What the workshop produces',
    whatItems: ['A shared diagnosis of the situation you are actually in — not the one you wish you were in', 'Explicit choices about where to focus and what to deprioritise', 'A coherent strategic logic the whole leadership team can articulate', 'Immediate next steps with ownership and accountability', 'A basis for ongoing strategy review rather than a one-off document'],
    formatsTitle: 'Formats',
    formats: [{
      name: 'Full Day',
      duration: '6-8 hours',
      desc: 'Diagnosis, choice-making, and initial action planning. Best for leadership teams with some existing strategic clarity who want to sharpen and align.',
      ideal: 'Leadership teams of 4-10 people'
    }, {
      name: 'Two Days',
      duration: '2 x full days',
      desc: 'The full arc — landscape mapping on day one, strategic choice-making and planning on day two, with an evening break for reflection between. Best when starting from significant uncertainty or misalignment.',
      ideal: 'Leadership teams navigating major change or transition'
    }],
    approachTitle: 'How it works',
    approach: [{
      step: '1',
      title: 'Pre-work',
      desc: 'I send a short diagnostic questionnaire to participants before the session. This surfaces existing assumptions, points of alignment and disagreement, and gives me a read on where the real work is.'
    }, {
      step: '2',
      title: 'The workshop',
      desc: 'Facilitated using a combination of structured exercises, open discussion and deliberate silent thinking time. I bring the frameworks and the questions. Your team brings the knowledge of your context.'
    }, {
      step: '3',
      title: 'The output',
      desc: 'A written summary of the strategic choices made, the logic behind them, the trade-offs acknowledged, and the immediate next steps committed to. Delivered within a week.'
    }],
    articleNote: 'The thinking behind this workshop is developed in the Lessons of Strategy series on Substack.',
    ctaTitle: "Let's talk",
    ctaDesc: 'Strategy workshops work best when they\'re designed around your specific situation. Tell me where you are and what you\'re trying to work out.',
    ctaCta: 'Get in touch'
  };
  const is = {
    badge: 'Stefna',
    title: 'Stefnum\u00f3tunar',
    titleHighlight: 'Vinnustofa',
    intro1: 'Flestar stefnum\u00f3tunarr\u00e1\u00f0stefnur skila skjali. Hva\u00f0 \u00fe\u00e6r skila sjaldan er stefna - raunveruleg val um hvar vi\u00f0 spilumsv\u00e6\u00f0i og hvernig vi\u00f0 vinnum, me\u00f0 erfi\u00f0ar m\u00e1lar\u00f3\u00f0ar ger\u00f0ar sk\u00fdrar.',
    intro2: 'Essa vinnustofa er bygg\u00f0 \u00e1 \u00feeim val\u00f0i saman, me\u00f0 r\u00e9ttum r\u00f6mmum og r\u00e9ttum spurningum. H\u00fan byggir \u00e1 Rumelt, Roger Martin og Wardley mapping - a\u00f0laga\u00f0 a\u00f0 st\u00e6r\u00f0 og samhengi \u00edslenskra fyrirt\u00e6kja.',
    heroCta: 'Hafa samband',
    whatTitle: 'Hva\u00f0 vinnustofan skilar',
    whatItems: ['Sameiginleg greining \u00e1 \u00feeirri st\u00f6\u00f0u sem \u00fer\u00fa\u00f0 ert raunverulega \u00ed', 'Sk\u00fdrar \u00e1kvar\u00f0anir um hvar \u00e1 a\u00f0 einbeita s\u00e9r og hva\u00f0 \u00e1 a\u00f0 l\u00e1ta bi\u00f0a', 'Samr\u00e6md stefa\u00f0 r\u00f6k sem allir \u00ed forystu geta bygg\u00f0 \u00e1', 'N\u00e6stu skref me\u00f0 sk\u00fdra \u00e1byrgur\u00f0', 'Grunnur fyrir l\u00f6fun\u00f0ar stefnum\u00f3tun frekar en eitt skjal'],
    formatsTitle: 'Snid',
    formats: [{
      name: 'Heill dagur',
      duration: '6-8 klukkustundir',
      desc: 'Greining, val og \u00e1\u00e6tlunarger\u00f0. Hentar best forystu teymum me\u00f0 einhverja stefnulegu skild\u00e1lknun sem vilja sk\u00e6ra og samr\u00e6ma.',
      ideal: 'Forystu teymi me\u00f0 4-10 manns'
    }, {
      name: 'Tveir dagar',
      duration: '2 x heill dagur',
      desc: 'Fullt b\u00f3g - kortlagning af landslagi \u00e1 fyrsta degi, stefnulegt val og skipulagsger\u00f0 \u00e1 \u00f6\u00f0rum degi.',
      ideal: 'Forystu teymi sem takast \u00e1 vi\u00f0 st\u00f3rar breytingar'
    }],
    approachTitle: 'Hvernig \u00fea\u00f0 virkar',
    approach: [{
      step: '1',
      title: 'Undirbúningur',
      desc: '\u00c9g sendi stutta gr\u00f3fun til \u00fe\u00e1tttakenda \u00e1\u00f0ur en vi\u00f0 m\u00f6tumst. \u00deetta ber\u00f0 upp forsendur og meinbug\u00f0i punkta.'
    }, {
      step: '2',
      title: 'Vinnustofan',
      desc: 'L\u00f3\u00f0su\u00f0 me\u00f0 blöndu af skipuleg\u00f0um \u00e6fingum, opnum umr\u00e6\u00f0um og hugsunarr\u00fdmi. \u00c9g kynni r\u00f6mmin og spurningarnar. Teymi\u00f0 ykkar kemur me\u00f0 \u00feekkingin.'
    }, {
      step: '3',
      title: '\u00datkomun',
      desc: 'Skriflegt dr\u00e1ttur \u00feeirra stefa\u00f0u vala sem ger\u00f0 voru og r\u00f3k a\u00f0 baki \u00feeim. Lef\u00f0 inn innan viku.'
    }],
    articleNote: 'Hugsuningin bak vi\u00f0 vinnustofuna er \u00fer\u00f3u\u00f0 \u00ed Lexíur stefnum\u00f3tunar r\u00f6\u00f0inni \u00e1 Substack.',
    ctaTitle: 'Vi\u00f0 skulum tala saman',
    ctaDesc: 'Stefnum\u00f3tunarvinnustofur virka best \u00feegar \u00fe\u00e6r eru hanna\u00f0ar um\u00fervert \u00fe\u00edna st\u00f6\u00f0u. Seg\u00f0u m\u00e9r hvar \u00fe\u00fa ert.',
    ctaCta: 'Hafa samband'
  };
  const c = language === 'is' ? is : en;
  return <PageTransition data-id="element-2004">
      <div className="pt-12 pb-24" data-id="element-2005">
        {/* Header */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20 text-center" data-id="element-2006">
          <Animate data-id="element-2007">
            <span className="inline-block px-4 py-2 bg-yellow-100 text-yellow-700 font-medium rounded-full text-sm mb-6" data-id="element-2008">
              {c.badge}
            </span>
            <h1 className="text-5xl md:text-6xl font-bold mb-6" data-id="element-2009">
              {c.title} <span className="text-coral" data-id="element-2010">{c.titleHighlight}</span>
            </h1>
            <p className="text-xl text-gray-600 leading-relaxed mb-4" data-id="element-2011">{c.intro1}</p>
            <p className="text-lg text-gray-500 leading-relaxed mb-8" data-id="element-2012">{c.intro2}</p>
            <Link to="/contact" data-id="element-2013">
              <Button rightIcon={<ArrowRight size={18} data-id="element-2015" />} data-id="element-2014">{c.heroCta}</Button>
            </Link>
          </Animate>
        </section>

        {/* What it produces */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-2016">
          <Animate data-id="element-2017">
            <Card className="p-10 border-2 border-yellow-200" data-id="element-2018">
              <div className="flex items-center gap-3 mb-6" data-id="element-2019">
                <div className="p-2 bg-yellow-100 rounded-xl" data-id="element-2020"><Target size={24} className="text-yellow-700" data-id="element-2021" /></div>
                <h2 className="text-2xl font-bold text-charcoal" data-id="element-2022">{c.whatTitle}</h2>
              </div>
              <div className="space-y-3" data-id="element-2023">
                {c.whatItems.map((item, i) => <div key={i} className="flex items-start gap-3" data-id="element-2024">
                    <div className="mt-1 p-1 rounded-full bg-yellow-100 text-yellow-700 flex-shrink-0" data-id="element-2025">
                      <Check size={12} strokeWidth={3} data-id="element-2026" />
                    </div>
                    <span className="text-gray-700 leading-relaxed" data-id="element-2027">{item}</span>
                  </div>)}
              </div>
            </Card>
          </Animate>
        </section>

        {/* Formats */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 mb-20" data-id="element-2028">
          <Animate className="mb-8" data-id="element-2029">
            <h2 className="text-3xl font-bold text-charcoal" data-id="element-2030">{c.formatsTitle}</h2>
          </Animate>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-2031">
            {c.formats.map((f, i) => <Animate key={i} delay={i * 0.1} data-id="element-2032">
                <Card className="p-8 h-full border-2 border-gray-100" data-id="element-2033">
                  <div className="flex items-center gap-3 mb-2" data-id="element-2034">
                    <span className="font-bold text-2xl text-charcoal" data-id="element-2035">{f.name}</span>
                  </div>
                  <div className="flex items-center gap-2 mb-4 text-yellow-700" data-id="element-2036">
                    <Clock size={16} data-id="element-2037" />
                    <span className="text-sm font-medium" data-id="element-2038">{f.duration}</span>
                  </div>
                  <p className="text-gray-600 leading-relaxed mb-4" data-id="element-2039">{f.desc}</p>
                  <div className="flex items-center gap-2 text-sm text-gray-400" data-id="element-2040">
                    <Users size={14} data-id="element-2041" />
                    <span data-id="element-2042">{f.ideal}</span>
                  </div>
                </Card>
              </Animate>)}
          </div>
        </section>

        {/* How it works */}
        <section className="bg-cream py-16 mb-20" data-id="element-2043">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2044">
            <Animate className="mb-10" data-id="element-2045">
              <h2 className="text-3xl font-bold text-charcoal" data-id="element-2046">{c.approachTitle}</h2>
            </Animate>
            <div className="space-y-6" data-id="element-2047">
              {c.approach.map((step, i) => <Animate key={i} delay={i * 0.1} data-id="element-2048">
                  <div className="flex gap-6" data-id="element-2049">
                    <div className="w-10 h-10 rounded-full bg-yellow-100 text-yellow-700 font-bold flex items-center justify-center flex-shrink-0 text-lg" data-id="element-2050">
                      {step.step}
                    </div>
                    <div data-id="element-2051">
                      <h3 className="font-bold text-charcoal text-lg mb-2" data-id="element-2052">{step.title}</h3>
                      <p className="text-gray-600 leading-relaxed" data-id="element-2053">{step.desc}</p>
                    </div>
                  </div>
                </Animate>)}
            </div>
          </div>
        </section>

        {/* Article link */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 mb-16" data-id="element-2054">
          <Animate data-id="element-2055">
            <Card className="p-6 border-l-4 border-yellow-400 bg-yellow-50/50" data-id="element-2056">
              <div className="flex items-center gap-3" data-id="element-2057">
                <Compass size={20} className="text-yellow-700 flex-shrink-0" data-id="element-2058" />
                <p className="text-gray-700" data-id="element-2059">
                  {c.articleNote}{' '}
                  <a href="https://helgig.substack.com" target="_blank" rel="noopener noreferrer" className="text-yellow-700 font-semibold hover:underline" data-id="element-2060">
                    Substack <ChevronRight size={14} className="inline" data-id="element-2061" />
                  </a>
                </p>
              </div>
            </Card>
          </Animate>
        </section>

        {/* CTA */}
        <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-2062">
          <Animate data-id="element-2063">
            <Card className="p-10 bg-gradient-to-br from-yellow-50 to-coral/5 border-0 text-center" data-id="element-2064">
              <h2 className="text-2xl font-bold mb-3" data-id="element-2065">{c.ctaTitle}</h2>
              <p className="text-gray-500 mb-6 max-w-lg mx-auto" data-id="element-2066">{c.ctaDesc}</p>
              <Link to="/contact" data-id="element-2067">
                <Button variant="primary" rightIcon={<ArrowRight size={18} data-id="element-2069" />} data-id="element-2068">{c.ctaCta}</Button>
              </Link>
            </Card>
          </Animate>
        </section>
      </div>
    </PageTransition>;
}